/**
 * ## Ref-counted Shared Store Base
 *
 * Lightweight lifecycle primitive for "create-on-demand" shared stores.
 * A store stays active while at least one consumer is attached, then can reset
 * itself when the last consumer detaches.
 *
 * @pattern `attach/detach reference counting`
 * @goal avoid global singleton coupling for feature-scoped stores
 * @see {@link attach}
 * @see {@link detach}
 */
export abstract class RefCountedSharedStoreBase {
  private readonly consumers = new Set<string>();

  /**
   * Registers a consumer token.
   * Triggers `onFirstConsumerAttached` when transitioning `0 -> 1` consumers.
   */
  attach(consumerId: string): void {
    if (!consumerId) {
      return;
    }

    const wasEmpty = this.consumers.size === 0;
    this.consumers.add(consumerId);
    if (wasEmpty && this.consumers.size > 0) {
      this.onFirstConsumerAttached();
    }
  }

  /**
   * Unregisters a consumer token.
   * Triggers `onLastConsumerDetached` when transitioning `1 -> 0` consumers.
   */
  detach(consumerId: string): void {
    if (!consumerId || !this.consumers.has(consumerId)) {
      return;
    }

    this.consumers.delete(consumerId);
    if (this.consumers.size === 0) {
      this.onLastConsumerDetached();
    }
  }

  /** Returns `true` when at least one active consumer is attached. */
  protected hasConsumers(): boolean {
    return this.consumers.size > 0;
  }

  /** Hook: override in child store when first consumer arrives. */
  protected onFirstConsumerAttached(): void {
    // Override when needed.
  }

  /** Hook: override in child store when all consumers are gone. */
  protected onLastConsumerDetached(): void {
    // Override when needed.
  }
}
